// Customer Master — Feb 2026 restructure (v2, polished layout).

import { useState, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import Layout from "../components/Layout";
import MasterExcelToolbar from "../components/MasterExcelToolbar";
import { Plus, PencilSimple, Trash, UsersThree } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const COUNTRIES = ["India", "United States", "United Kingdom", "Germany", "China", "Japan", "France", "Italy", "Spain", "Australia", "Canada", "Brazil", "Mexico", "Russia", "South Africa", "UAE", "Saudi Arabia", "Singapore", "Malaysia", "Vietnam", "Thailand", "South Korea", "Indonesia", "Turkey", "Poland", "Netherlands", "Belgium", "Sweden", "Norway", "Denmark", "Finland", "Switzerland", "Austria", "Portugal", "Greece", "Ireland", "Israel", "Egypt", "Nigeria", "Kenya", "Argentina", "Chile", "Colombia", "Philippines", "Pakistan", "Bangladesh", "Sri Lanka", "Nepal"];
const IN_STATES = ["Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi", "Jammu & Kashmir", "Ladakh", "Puducherry", "Chandigarh", "Andaman & Nicobar", "Dadra & Nagar Haveli", "Daman & Diu", "Lakshadweep"];

const blankForm = () => ({
  name: "", vendor_code: "", customer_type: "", location: "",
  address: "",
  country: "India", state: "", pin_code: "", gst_no: "",
});

function SearchableSelect({ value, onChange, options, placeholder, testId }) {
  const listId = `dl-${testId}`;
  return (
    <>
      <input value={value} onChange={e => onChange(e.target.value)}
        list={listId} placeholder={placeholder}
        className="w-full min-h-[38px] border border-[#E2E8F0] rounded px-2 text-xs"
        data-testid={testId} />
      <datalist id={listId}>{options.map(o => <option key={o} value={o} />)}</datalist>
    </>
  );
}

export default function CustomerMaster() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blankForm());
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showAddType, setShowAddType] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, t] = await Promise.all([
        axios.get(`${API}/customer-master`),
        axios.get(`${API}/customer-types`),
      ]);
      setRows(c.data); setTypes(t.data);
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const openAdd = () => { setEditingId(null); setForm(blankForm()); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name || "", vendor_code: r.vendor_code || "",
      customer_type: r.customer_type || "", location: r.location || "",
      address: r.address || "",
      country: r.country || "India", state: r.state || "", pin_code: r.pin_code || "",
      gst_no: r.gst_no || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Customer name is required");
    try {
      if (editingId) {
        await axios.put(`${API}/customer-master/${editingId}`, form);
        toast.success("Customer updated");
      } else {
        await axios.post(`${API}/customer-master`, form);
        toast.success("Customer added");
      }
      setDialogOpen(false); setForm(blankForm()); setEditingId(null); loadAll();
      window.dispatchEvent(new Event("customers:changed"));
    } catch (e) { toast.error("Save failed: " + (e.response?.data?.detail || e.message)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this customer?")) return;
    try { await axios.delete(`${API}/customer-master/${id}`); toast.success("Deleted"); loadAll();
      window.dispatchEvent(new Event("customers:changed"));
    } catch { toast.error("Delete failed"); }
  };

  const addType = async () => {
    if (!newTypeName.trim()) return;
    try {
      await axios.post(`${API}/customer-types`, { name: newTypeName.trim() });
      toast.success("Customer type added");
      setNewTypeName(""); setShowAddType(false);
      const t = await axios.get(`${API}/customer-types`); setTypes(t.data);
    } catch (e) { toast.error("Failed: " + (e.response?.data?.detail || e.message)); }
  };

  const isIN = form.country === "India";

  return (
    <Layout title="Customer Master">
      <div className="p-6 space-y-5 max-w-[1400px] mx-auto">
        <PageHeader
          icon={UsersThree}
          title="Customer Master"
          subtitle="Every customer used across parts, sales orders and invoices lives here."
          rightSlot={
            <div className="flex items-center gap-2 flex-wrap">
              <MasterExcelToolbar basePath="/customer-master" entityLabel="Customers"
                onImportDone={() => { loadAll(); window.dispatchEvent(new Event("customers:changed")); }} size="sm" />
              <Button className="bg-[#1D2951] hover:bg-[#2A3B73] text-white gap-1 h-9" onClick={openAdd} data-testid="cm-add-btn">
                <Plus size={14} /> Add Customer
              </Button>
            </div>
          }
        />

        <Card className="border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-[#475569] text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Name</th>
                  <th className="px-3 py-3 text-left font-semibold">Vendor Code</th>
                  <th className="px-3 py-3 text-left font-semibold">Type</th>
                  <th className="px-3 py-3 text-left font-semibold">Location</th>
                  <th className="px-3 py-3 text-left font-semibold">Address</th>
                  <th className="px-3 py-3 text-left font-semibold">State</th>
                  <th className="px-3 py-3 text-left font-semibold">Country</th>
                  <th className="px-3 py-3 text-left font-semibold">PIN</th>
                  <th className="px-3 py-3 text-left font-semibold">GST No</th>
                  <th className="px-3 py-3 text-center font-semibold w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {loading && <tr><td colSpan={10} className="py-10 text-center text-[#64748B]">Loading…</td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={10} className="py-12 text-center text-[#94A3B8]">No customers yet — click <b>Add Customer</b> to create your first one.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.id || r.name} className="hover:bg-[#F8FAFC]" data-testid={`cm-row-${r.name}`}>
                    <td className="px-4 py-3 font-semibold text-[#0F172A]">{r.name}</td>
                    <td className="px-3 py-3 font-mono text-xs text-[#475569]">{r.vendor_code || "—"}</td>
                    <td className="px-3 py-3 text-[#475569]">{types.find(t => t.code === r.customer_type)?.name || "—"}</td>
                    <td className="px-3 py-3 text-[#475569]">{r.location || "—"}</td>
                    <td className="px-3 py-3 text-[#475569] max-w-[240px] truncate" title={r.address || ""}>{r.address || "—"}</td>
                    <td className="px-3 py-3 text-[#475569]">{r.state || "—"}</td>
                    <td className="px-3 py-3 text-[#475569]">{r.country || "—"}</td>
                    <td className="px-3 py-3 font-mono text-xs text-[#475569]">{r.pin_code || "—"}</td>
                    <td className="px-3 py-3 font-mono text-xs text-[#475569]">{r.gst_no || "—"}</td>
                    <td className="px-3 py-3 text-center whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)}><PencilSimple size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-[#DC2626]"><Trash size={14} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Add / Edit Modal */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit Customer" : "Add Customer"}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <FormSection title="Identity">
              <FieldGrid cols={3}>
                <Field label="Customer Name *">
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="cm-name" />
                </Field>
                <Field label="Vendor Code">
                  <Input value={form.vendor_code} onChange={e => setForm({ ...form, vendor_code: e.target.value })} data-testid="cm-vendor" />
                </Field>
                <Field label="Type of Customer">
                  <div className="flex gap-1">
                    <select value={form.customer_type}
                      onChange={e => setForm({ ...form, customer_type: e.target.value })}
                      className="flex-1 min-h-[38px] border border-[#E2E8F0] rounded px-2 text-xs bg-white"
                      data-testid="cm-type">
                      <option value="">— Select —</option>
                      {types.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
                    </select>
                    <Button size="sm" variant="outline" onClick={() => setShowAddType(true)} title="Add new type"><Plus size={14} /></Button>
                  </div>
                </Field>
              </FieldGrid>
            </FormSection>

            <FormSection title="Address">
              <FieldGrid cols={3}>
                <Field label="Location">
                  <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="City / Area" data-testid="cm-location" />
                </Field>
                <Field label="Address" span={2}>
                  <Input value={form.address}
                    onChange={e => setForm({ ...form, address: e.target.value })}
                    placeholder="Street, building, landmark…"
                    data-testid="cm-address" />
                </Field>
                {isIN && (
                  <Field label="State">
                    <SearchableSelect value={form.state}
                      onChange={v => setForm({ ...form, state: v })}
                      options={IN_STATES} placeholder="Type / pick" testId="cm-state" />
                  </Field>
                )}
                <Field label="Country">
                  <SearchableSelect value={form.country}
                    onChange={v => setForm({ ...form, country: v, state: v === "India" ? form.state : "" })}
                    options={COUNTRIES} placeholder="Type / pick" testId="cm-country" />
                </Field>
                {isIN && (
                  <Field label="PIN Code">
                    <Input value={form.pin_code} onChange={e => setForm({ ...form, pin_code: e.target.value })} data-testid="cm-pin" />
                  </Field>
                )}
                <Field label="GST No" hint="15-char GSTIN">
                  <Input value={form.gst_no}
                    onChange={e => setForm({ ...form, gst_no: e.target.value.toUpperCase() })}
                    placeholder="e.g. 27ABCDE1234F1Z5"
                    maxLength={20}
                    className="font-mono"
                    data-testid="cm-gst-no" />
                </Field>
              </FieldGrid>
            </FormSection>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#E2E8F0] mt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#1D2951] text-white" onClick={save} data-testid="cm-save">
              <Plus size={14} /> {editingId ? "Save Changes" : "Add Customer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Nested Add Customer Type dialog — stacks ABOVE the primary modal. */}
      <Dialog open={showAddType} onOpenChange={setShowAddType}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-[#1D2951]">Add Customer Type</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-1">
            <div>
              <Label className="text-xs font-semibold">Name</Label>
              <Input value={newTypeName} onChange={e => setNewTypeName(e.target.value)}
                placeholder="e.g. Aftermarket, Government…" data-testid="new-cust-type" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddType(false)}>Cancel</Button>
              <Button className="bg-[#1D2951] text-white" onClick={addType} data-testid="save-new-cust-type">Add</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function PageHeader({ icon: Icon, title, subtitle, rightSlot }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="flex items-start gap-3">
        {Icon && <div className="p-2 rounded-lg bg-[#1D2951]/5 text-[#1D2951]"><Icon size={20} weight="fill" /></div>}
        <div>
          <h1 className="text-2xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>{title}</h1>
          {subtitle && <p className="text-xs text-[#64748B] mt-0.5 max-w-2xl">{subtitle}</p>}
        </div>
      </div>
      {rightSlot}
    </div>
  );
}
function FormSection({ title, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">{title}</h3>
        {hint && <span className="text-[10px] text-[#94A3B8] italic">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
function FieldGrid({ cols = 3, children }) {
  const grid = { 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4" }[cols] || "grid-cols-3";
  return <div className={`grid ${grid} gap-3`}>{children}</div>;
}
function Field({ label, hint, span = 1, children }) {
  const spanCls = span === 2 ? "col-span-2" : span === 3 ? "col-span-3" : "";
  return (
    <div className={spanCls}>
      <Label className="text-[11px] font-semibold text-[#334155] flex items-center gap-1">
        {label}{hint && <span className="text-[10px] font-normal text-[#94A3B8]">({hint})</span>}
      </Label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
